import { ref, reactive } from 'vue'
import { flagError, type ErrorSink } from 'error-handling'
import { formErrorMessage } from '../utils/cmsFormError'
import { postFormSubmit } from '../utils/cmsBusy'
import { readUploadResponse, type WebformUploadResult } from '../utils/webformUpload'

export const formData = reactive<{ data: any }>({ data: {} })

// 'lookup-incomplete': the server refused the submission because its address
// lookup could not fill a required field the client had stopped validating on
// the strength of an earlier lookup. Nothing was written; the form puts those
// fields back in front of the donor.
// 'cms-busy': the CMS was still refusing writes (a deploy) after ~90 s of retries.
type SubmissionCase =
  | 'added'
  | 'already-added'
  | 'crm-error'
  | 'lookup-incomplete'
  | 'cms-busy'
  | null

type SendFormErrorData = {
  recaptchaReason?: string
  recaptchaInvalidReason?: string
}

export const useForm = (config: { cmsBasePath: string; rollbar?: ErrorSink }) => {
  const isLoading = ref(false)
  // True while waiting to re-send because the CMS was busy.
  const isRetrying = ref(false)
  const error = ref('')
  const submissionCase = ref<SubmissionCase>(null)
  const lastErrorData = ref<SendFormErrorData | null>(null)

  // Keyed by element name only, so a field that keeps re-evaluating with the
  // same wrong type (the normal case — every keystroke re-runs this) reports
  // once per form instance instead of flooding Rollbar. Scoped to this
  // composable call, so a fresh mount gets one report again — cheap enough,
  // and it re-surfaces a bug that's still there after a reload.
  const warnedFieldTypes = new Set<string>()
  function warnUnexpectedFieldType(elementName: string, value: unknown) {
    const valueType = typeof value
    console.warn(`Unexpected type "${valueType}" for element "${elementName}"`)
    if (warnedFieldTypes.has(elementName)) return
    warnedFieldTypes.add(elementName)
    // A webform's #states config claims a field is a string/boolean where the
    // data says otherwise — a real mismatch between the form definition and
    // its data, not a donor-triggerable case, so it's worth a look every time
    // it happens rather than staying console-only.
    flagError(
      new Error(`Unexpected type "${valueType}" evaluating conditional state`),
      'useForm.getStatesForData',
      config.rollbar,
      { elementName, valueType },
    )
  }

  const sendForm = async (formId: string, data: any) => {
    error.value = ''
    submissionCase.value = null
    lastErrorData.value = null
    isLoading.value = true

    let dataResponse: any
    let reply: Awaited<ReturnType<typeof postFormSubmit>>
    try {
      reply = await postFormSubmit({ ...data, webform_id: formId }, () => (isRetrying.value = true))
      dataResponse = reply.body
    } catch (e) {
      // The request never reached the server (offline, a proxy's HTML error
      // page), so only the client can report it. Without this the button spins
      // forever.
      flagError(e, 'useForm.sendForm', config.rollbar)
      error.value = 'Network error'
      isLoading.value = false
      return false
    } finally {
      isRetrying.value = false
    }
    if (reply.busy) {
      // The server only logs each busy answer at info level; this is where a
      // submission is actually lost.
      flagError(
        new Error('formSubmit: CMS still busy after client retries'),
        'useForm.sendForm',
        config.rollbar,
        {
          webform_id: formId,
        },
      )
      submissionCase.value = 'cms-busy'
      isLoading.value = false
      return false
    }
    // After a busy answer, the first attempt has already written to the CRM, so
    // the re-send hears "already registered" about its own submission.
    if (reply.retried && dataResponse.data?.alreadyRegistered === true) {
      submissionCase.value = 'added'
      isLoading.value = false
      return true
    }
    if (dataResponse.statusCode >= 400) {
      const errData = dataResponse.data
      error.value = formErrorMessage(dataResponse)
      if (errData && typeof errData === 'object') {
        lastErrorData.value = {
          recaptchaReason:
            typeof errData.recaptchaReason === 'string' ? errData.recaptchaReason : undefined,
          recaptchaInvalidReason:
            typeof errData.recaptchaInvalidReason === 'string'
              ? errData.recaptchaInvalidReason
              : undefined,
        }
      }
      isLoading.value = false
      return false
    }

    // Parse structured response (CRM forms return { success, case })
    // Non-CRM forms return bare 200 — treat as success
    const answered = (dataResponse.case as SubmissionCase | undefined) || 'added'
    submissionCase.value = reply.retried && answered === 'already-added' ? 'added' : answered
    isLoading.value = false

    return dataResponse.success !== false
  }

  const uploadFile = async ({
    file,
    webformId,
    fieldName,
  }: {
    file: any
    webformId: string
    fieldName: string
  }): Promise<WebformUploadResult> => {
    try {
      const filename = file.name
        .replace(/[^\w -.]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase()

      const response = await fetch(
        `${config.cmsBasePath}/webform_rest/${webformId}/upload/${fieldName}?_format=json`,
        {
          headers: {
            'Content-Type': 'application/octet-stream',
            'Content-Disposition': `file; filename="${filename}"`,
          },
          method: 'POST',
          body: file,
        },
      )
      return await readUploadResponse(response)
    } catch (error) {
      // Was a bare console.log → invisible. Like the other refresh-common
      // composables (and unlike app-level ones), this omits $rollbar — shared
      // composables don't reach for useNuxtApp. On the server flagError dispatches
      // to Rollbar via the default logger; client-side it's structured console
      // output until a shared client logger lands. The caller still proceeds
      // without the attachment.
      flagError(error, 'useForm.uploadFile')
      return { ok: false, reason: null }
    }
  }

  function isObject(item: any) {
    return typeof item === 'object' && !Array.isArray(item) && item !== null
  }

  const updateDataForm = (formId: string, data: any) => {
    formData.data[formId] = data
  }

  // Returns substring with the element selector.
  function getElementName(string: string) {
    const match = string.match(/\[name=["|']([A-z][A-z\d-_.:]*)["|']\]$/)
    return match && match[1]
  }

  // Checks if the condition is true or not and returns a boolean value.
  function getConditionState(trigger: any, value: any) {
    const triggerKey = Object.keys(trigger)[0]
    if (!triggerKey) return false
    if (isObject(trigger[triggerKey])) {
      switch (Object.keys(trigger[triggerKey])[0]) {
        case 'pattern': {
          if (typeof value !== 'string') {
            return false
          }

          const re = new RegExp(trigger['value']['pattern'])
          return re.test(value)
        }
        case '!pattern': {
          if (typeof value !== 'string') {
            return false
          }

          const re = new RegExp(trigger['value']['pattern'])
          return !re.test(value)
        }
        case 'less':
          return value < trigger['value']['less']
        case 'less_equal':
          return value <= trigger['value']['less_equal']
        case 'greater':
          return value > trigger['value']['greater']
        case 'greater_equal':
          return value >= trigger['value']['greater_equal']
        case 'between':
        case '!between': {
          const betweenValues = trigger['value']['between']
          const min = betweenValues.substring(0, betweenValues.indexOf(':'))
          const max = betweenValues.substring(betweenValues.indexOf(':'), betweenValues.length - 1)
          if (Object.keys(trigger)[0] == 'between') {
            return value >= min && value <= max
          } else {
            return value < min || value > max
          }
        }
      }
    } else {
      switch (trigger && Object.keys(trigger)[0]) {
        case 'empty':
          return !value
        case 'filled':
          return !!value
        case 'checked':
          return value == true
        case 'unchecked':
          return value == false
        case 'value':
          return value == trigger['value']
        case '!value':
          return value != trigger['value']
      }
    }
  }

  const getStateConditions = (states: any, data: any) => {
    const allConditionsForTrue: any = {}
    const anyConditionForTrue: any = {}
    const oneConditionForTrue: any = {}
    let webformStates

    if (states && !Object.keys(states).includes('headers')) {
      webformStates = states
      for (const effect of Object.keys(webformStates)) {
        allConditionsForTrue[effect] = {}
        anyConditionForTrue[effect] = {}
        oneConditionForTrue[effect] = {}
        // Loop through indexes or selectors.
        for (const indexOrSelector of Object.keys(webformStates[effect])) {
          // If indexOrSelector is not an index, then ALL conditions must be true
          // for the attribute to take effect.
          if (isNaN(Number(indexOrSelector))) {
            const elementName: any = getElementName(indexOrSelector)

            if (
              Object.prototype.hasOwnProperty.call(data, elementName) &&
              typeof data[elementName] !== 'string' &&
              typeof data[elementName] !== 'boolean'
            ) {
              warnUnexpectedFieldType(elementName, data[elementName])
              continue
            }

            allConditionsForTrue[effect][elementName] = getConditionState(
              webformStates[effect][indexOrSelector],
              data[elementName] ?? undefined,
            )
          } else if (
            webformStates[effect][indexOrSelector] !== 'or' &&
            webformStates[effect][indexOrSelector] !== 'xor'
          ) {
            const selectorString = Object.keys(webformStates[effect][indexOrSelector])[0] ?? ''
            const elementName: any = getElementName(selectorString)
            const trigger = Object.values(webformStates[effect][indexOrSelector])[0]

            if (
              Object.prototype.hasOwnProperty.call(data, elementName) &&
              typeof data[elementName] !== 'string' &&
              typeof data[elementName] !== 'boolean'
            ) {
              warnUnexpectedFieldType(elementName, data[elementName])
              continue
            }

            if (Object.values(webformStates[effect]).includes('or')) {
              anyConditionForTrue[effect][elementName] = getConditionState(
                trigger,
                data[elementName] ?? undefined,
              )
            } else if (Object.values(webformStates[effect]).includes('xor')) {
              oneConditionForTrue[effect][elementName] = getConditionState(
                trigger,
                data[elementName] ?? undefined,
              )
            }
          }
        }
      }
    }
    return {
      allConditionsForTrue,
      anyConditionForTrue,
      oneConditionForTrue,
      webformStates,
    }
  }

  const getEffect = (effect: any) => {
    switch (effect) {
      case 'invisible':
      case 'invisible-slide':
      case '!visible':
        return { '#access': false }
      case 'visible':
      case 'visible-slide':
      case '!invisible':
        return { '#access': true }
      case 'enabled':
      case '!disabled':
        return { '#disabled': false }
      case 'disabled':
      case '!enabled':
        return { '#disabled': true }
      case 'required':
      case '!optional':
        return { '#required': true }
      case 'optional':
      case '!required':
        return { '#required': false }
      case 'checked':
      case '!unchecked':
        return { '#checked': true }
      case 'unchecked':
      case '!checked':
        return { '#unchecked': true }
      case 'readonly':
      case '!readwrite':
        return { '#readonly': true }
      case 'readwrite':
      case '!readonly':
        return { '#readonly': false }
    }
  }

  const getStatesForData = (states: any, data: any) => {
    // Initialize the list of conditions and set whether they are true or not.
    const { allConditionsForTrue, anyConditionForTrue, oneConditionForTrue, webformStates } =
      getStateConditions(states, data)

    if (
      !webformStates ||
      Object.keys(webformStates).length === 0 ||
      !Object.keys(webformStates)[0]?.length
    ) {
      return { '#access': true }
    }

    const effects = {}

    // Set the state accordingly based on our list of conditions.
    for (const effect of Object.keys(webformStates)) {
      // Check if EVERY condition is true.
      if (
        Object.keys(allConditionsForTrue[effect]).length &&
        Object.values(allConditionsForTrue[effect]).every(value => value === true)
      ) {
        Object.assign(effects, getEffect(effect))
        // Check if ANY condition is true (OR).
      } else if (
        Object.keys(anyConditionForTrue[effect]).length &&
        Object.values(anyConditionForTrue[effect]).includes(true)
      ) {
        Object.assign(effects, getEffect(effect))
        // Check if ONE condition is true (XOR).
      } else if (Object.keys(oneConditionForTrue[effect]).length) {
        const filterByTrue = Object.values(oneConditionForTrue[effect]).filter(item => item == true)
        if (filterByTrue.length == 1) {
          Object.assign(effects, getEffect(effect))
        } else {
          const falseEffect = '!' + effect
          Object.assign(effects, getEffect(falseEffect))
        }
      } else {
        const falseEffect = '!' + effect
        Object.assign(effects, getEffect(falseEffect))
      }
    }
    return effects
  }

  return {
    isLoading,
    isRetrying,
    sendForm,
    error,
    submissionCase,
    lastErrorData,
    updateDataForm,
    getStatesForData,
    uploadFile,
  }
}
